import {
  AgentChat,
  type AgentChatBranding,
  type AgentChatHandle,
  type OnAgentEvent,
  useAgentChat,
} from '@kapaai/agent-react';
import { Box, rem } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconArrowsMaximize, IconArrowsMinimize } from '@tabler/icons-react';
import { Button } from 'design-system/button/button';
import { DESKTOP_MEDIA_QUERY } from 'lib/constants/ui';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const AGENT_PANEL_WIDTH = 476;
const TRANSITION_MS = 300;
const TRANSITION = `transform ${TRANSITION_MS}ms ease, left ${TRANSITION_MS}ms ease`;

interface AgentUIContextValue {
  isOpen: boolean;
  isFullScreen: boolean;
  togglePanel: () => void;
  openPanel: () => void;
  closePanel: () => void;
  toggleFullScreen: () => void;
}

const AgentUIContext = createContext<AgentUIContextValue | null>(null);

export const useAgentUI = (): AgentUIContextValue => {
  const context = useContext(AgentUIContext);
  if (!context) {
    throw new Error('useAgentUI must be used within an AgentUIProvider');
  }
  return context;
};

export const useAgent = () => {
  return { ...useAgentUI(), ...useAgentChat() };
};

interface AgentUIProviderProps {
  children: React.ReactNode;
  onEvent?: OnAgentEvent;
}

export const AgentUIProvider = ({
  children,
  onEvent,
}: AgentUIProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const togglePanel = useCallback(() => {
    setIsOpen((prev) => {
      onEvent?.({
        type: prev ? 'panel_closed' : 'panel_opened',
        data: {} as Record<string, never>,
      });
      return !prev;
    });
  }, [onEvent]);
  const openPanel = useCallback(() => {
    setIsOpen(true);
    onEvent?.({
      type: 'panel_opened',
      data: {} as Record<string, never>,
    });
  }, [onEvent]);
  const closePanel = useCallback(() => {
    setIsOpen(false);
    onEvent?.({
      type: 'panel_closed',
      data: {} as Record<string, never>,
    });
  }, [onEvent]);
  const toggleFullScreen = useCallback(
    () => setIsFullScreen((prev) => !prev),
    [],
  );

  const value = useMemo<AgentUIContextValue>(
    () => ({
      isOpen,
      isFullScreen,
      togglePanel,
      openPanel,
      closePanel,
      toggleFullScreen,
    }),
    [
      isOpen,
      isFullScreen,
      togglePanel,
      openPanel,
      closePanel,
      toggleFullScreen,
    ],
  );

  return (
    <AgentUIContext.Provider value={value}>{children}</AgentUIContext.Provider>
  );
};

export const AgentPanelSpacer = () => {
  const { isOpen, isFullScreen } = useAgentUI();
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY, true, {
    getInitialValueInEffect: false,
  });

  return (
    <Box
      sx={{
        // No reserved space when the panel is fullscreen (incl. below lg).
        width: isOpen && !isFullScreen && isDesktop ? AGENT_PANEL_WIDTH : 0,
        flexShrink: 0,
        transition: `width ${TRANSITION_MS}ms ease`,
      }}
    />
  );
};

interface AgentPanelWrapperProps {
  branding?: AgentChatBranding;
}

export const AgentPanelWrapper = ({ branding }: AgentPanelWrapperProps) => {
  const { isOpen, isFullScreen, closePanel, toggleFullScreen } = useAgentUI();
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY, true, {
    getInitialValueInEffect: false,
  });
  // Below lg the 476px side panel is too cramped, so always render fullscreen.
  const fullScreen = isFullScreen || !isDesktop;
  const chatRef = useRef<AgentChatHandle>(null);

  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      chatRef.current?.focus();
    }, TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [isOpen]);

  const fullScreenAction = (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggleFullScreen}
      title={isFullScreen ? 'Exit full screen' : 'Full screen'}
    >
      {isFullScreen ? (
        <IconArrowsMinimize size={18} stroke={1.5} />
      ) : (
        <IconArrowsMaximize size={18} stroke={1.5} />
      )}
    </Button>
  );

  return (
    <Box
      bg="var(--color-surface-elevated)"
      sx={() => ({
        position: 'fixed',
        top: 0,
        bottom: 0,
        left: fullScreen ? 0 : `calc(100% - ${AGENT_PANEL_WIDTH}px)`,
        right: 0,
        zIndex: 200,
        borderLeft: fullScreen
          ? 'none'
          : `${rem(1)} solid var(--color-line-strong)`,
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        visibility: isOpen ? ('visible' as const) : ('hidden' as const),
        transition: isOpen
          ? `${TRANSITION}, visibility 0s`
          : `${TRANSITION}, visibility 0s ${TRANSITION_MS}ms`,
      })}
    >
      <AgentChat
        ref={chatRef}
        branding={branding}
        onClose={closePanel}
        headerActions={isDesktop ? fullScreenAction : undefined}
        maxContentWidth={fullScreen ? undefined : 0}
      />
    </Box>
  );
};
